import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCheckoutStatus } from "@/lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      if (attempts.current >= 8) { if (active) setState("timeout"); return; }
      attempts.current += 1;
      try {
        const res = await getCheckoutStatus(sessionId);
        if (!active) return;
        if (res.payment_status === "paid") {
          setState("paid");
          setTimeout(() => navigate(`/report/${res.report_id}`), 1200);
          return;
        }
        if (res.status === "expired") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) { setTimeout(poll, 2000); }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, navigate]);

  return (
    <div className="mx-auto max-w-md px-4 py-24 text-center">
      {state === "checking" && (<><Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--atlas-navy))] mx-auto" /><h1 className="mt-4 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))]">Confirming your payment…</h1><p className="mt-2 text-muted-foreground">Please don&apos;t close this window. This usually takes a few seconds.</p></>)}
      {state === "paid" && (<><CheckCircle2 className="h-10 w-10 text-[hsl(var(--success))] mx-auto" /><h1 className="mt-4 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))]">Payment confirmed</h1><p className="mt-2 text-muted-foreground">Unlocking your full report…</p></>)}
      {state === "timeout" && (<><Loader2 className="h-8 w-8 text-[hsl(var(--atlas-steel))] mx-auto" /><h1 className="mt-4 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))]">Still processing</h1><p className="mt-2 text-muted-foreground">Your payment is taking longer than usual. You can retrieve your report by email shortly, or refresh this page.</p><Button className="mt-4" onClick={() => window.location.reload()}>Refresh</Button></>)}
      {state === "failed" && (<><XCircle className="h-10 w-10 text-rose-500 mx-auto" /><h1 className="mt-4 font-display text-2xl font-semibold text-[hsl(var(--atlas-navy))]">Payment not completed</h1><p className="mt-2 text-muted-foreground">Your checkout was cancelled or expired.</p><Button className="mt-4 bg-[hsl(var(--atlas-navy))] text-white" onClick={() => navigate("/check")}>Try again</Button></>)}
    </div>
  );
}
